import { useState, useCallback, useEffect } from 'react';
import { LuPencil, LuTrash2, LuPlus, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Button } from '@/components/Button';
import type { CustomField } from '../types';
import CustomFieldItem from './CustomFieldItem';

interface CustomFieldListProps {
  categoryName: string;
  fields: CustomField[];
  onAddField: () => void;
  onEditField: (field: CustomField) => void;
  onDeleteField: (field: CustomField) => void;
  onEditCategory: () => void;
  onDeleteCategory: () => void;
  onReorder: (fields: CustomField[]) => void;
  onMoveCategoryUp?: () => void;
  onMoveCategoryDown?: () => void;
  canMoveCategoryUp?: boolean;
  canMoveCategoryDown?: boolean;
  isReorderingCategory?: boolean;
}

/**
 * カスタムフィールド一覧コンポーネント
 */
export default function CustomFieldList({
  categoryName,
  fields,
  onAddField,
  onEditField,
  onDeleteField,
  onEditCategory,
  onDeleteCategory,
  onReorder,
  onMoveCategoryUp,
  onMoveCategoryDown,
  canMoveCategoryUp = false,
  canMoveCategoryDown = false,
  isReorderingCategory = false,
}: CustomFieldListProps) {
  const [localFields, setLocalFields] = useState(fields);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  // fieldsが変更されたらlocalFieldsも更新
  useEffect(() => {
    setLocalFields(fields);
  }, [fields]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        setLocalFields((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          const newItems = arrayMove(items, oldIndex, newIndex);
          // 順序変更をコールバック
          onReorder(newItems);
          return newItems;
        });
      }
    },
    [onReorder]
  );
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      {/* ヘッダー */}
      <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-black">{categoryName}</h2>
        <div className="flex gap-3">
          {onMoveCategoryUp && onMoveCategoryDown && (
            <>
              <Button
                onClick={onMoveCategoryUp}
                color="secondary"
                size="sm"
                disabled={!canMoveCategoryUp || isReorderingCategory}
                className="flex items-center gap-1"
              >
                <LuChevronLeft size={16} />
                <span>前に移動</span>
              </Button>
              <Button
                onClick={onMoveCategoryDown}
                color="secondary"
                size="sm"
                disabled={!canMoveCategoryDown || isReorderingCategory}
                className="flex items-center gap-1"
              >
                <LuChevronRight size={16} />
                <span>後ろに移動</span>
              </Button>
            </>
          )}
          <Button
            onClick={onEditCategory}
            color="tertiary"
            size="sm"
            disabled={isReorderingCategory}
          >
            <LuPencil size={16} />
            <span>区分を編集</span>
          </Button>
          {fields.length === 0 && (
            <Button
              onClick={onDeleteCategory}
              color="danger-secondary"
              size="sm"
              style={{ border: '0px' }}
            >
              <LuTrash2 size={16} />
              <span>区分を削除</span>
            </Button>
          )}
        </div>
      </div>

      {/* フィールド一覧 */}
      {fields.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-lg bg-gray-50 py-12">
          <p className="text-base text-gray-600">項目が登録されていません</p>
          <Button onClick={onAddField} color="secondary" size="sm">
            <LuPlus size={16} />
            <span>項目を追加</span>
          </Button>
        </div>
      ) : (
        <div className="space-y-0">
          {/* ヘッダー行 */}
          <div className="flex items-center gap-6 border-b border-solid border-gray-100 bg-white py-3 pr-4 pl-3">
            <div className="w-5" /> {/* ドラッグハンドルのスペース */}
            <div className="w-[350px] text-sm text-gray-600">タイトル・データ入力例</div>
            <div className="flex-1 text-sm text-gray-600">情報タイプ・選択肢</div>
            <div className="w-20" /> {/* アクションボタンのスペース */}
          </div>

          {/* フィールド行（ドラッグ可能） */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={localFields} strategy={verticalListSortingStrategy}>
              {localFields.map((field) => (
                <CustomFieldItem
                  key={field.id}
                  field={field}
                  onEditField={onEditField}
                  onDeleteField={onDeleteField}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* 項目追加ボタン */}
          <div className="flex justify-end pt-6">
            <Button onClick={onAddField} color="secondary" size="sm">
              <LuPlus size={16} />
              <span>項目を追加</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
